import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usciteApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus,
  Wallet,
  CalendarDays,
  Loader2,
  Trash2,
  Edit,
  Receipt,
  ChevronRight,
  Banknote,
  CreditCard,
  Building2,
  HelpCircle,
  Filter,
  X
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../lib/utils';

const CATEGORIE = [
  { value: 'Forni/Attrezzature', icon: '🔧' },
  { value: 'Materie prime', icon: '🥫' },
  { value: 'Personale', icon: '👥' },
  { value: 'Manutenzione', icon: '🔨' },
  { value: 'Utenze', icon: '💡' },
  { value: 'Affitto', icon: '🏠' },
  { value: 'Extra', icon: '📦' },
  { value: 'Altro', icon: '📋' },
];

const METODI_PAGAMENTO = [
  { value: 'contanti', label: 'Contanti', icon: Banknote },
  { value: 'POS', label: 'POS', icon: CreditCard },
  { value: 'bonifico', label: 'Bonifico', icon: Building2 },
  { value: 'altro', label: 'Altro', icon: HelpCircle },
];

const TURNI = [
  { value: 'pranzo', label: 'Pranzo' },
  { value: 'cena', label: 'Cena' },
  { value: 'giorno intero', label: 'Giorno intero' },
  { value: 'non specificato', label: 'Non specificato' },
];

const UsciteCassaPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [uscite, setUscite] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dataInizio, setDataInizio] = useState(startOfMonth(subMonths(new Date(), 2)));
  const [dataFine, setDataFine] = useState(endOfMonth(new Date()));
  const [calendarOpenInizio, setCalendarOpenInizio] = useState(false);
  const [calendarOpenFine, setCalendarOpenFine] = useState(false);
  const [activeFilter, setActiveFilter] = useState('last-3-months');
  
  const [formData, setFormData] = useState({
    data_spesa: new Date(),
    importo: '',
    metodo_pagamento: 'contanti',
    categoria: 'Altro',
    descrizione: '',
    collegata_a_turno: 'non specificato'
  });

  const canModify = ['admin', 'manager'].includes(user?.ruolo);

  const setQuickFilter = (filter) => {
    setActiveFilter(filter);
    const now = new Date();
    switch (filter) {
      case 'this-month':
        setDataInizio(startOfMonth(now));
        setDataFine(endOfMonth(now));
        break;
      case 'last-month':
        const lastMonth = subMonths(now, 1);
        setDataInizio(startOfMonth(lastMonth));
        setDataFine(endOfMonth(lastMonth));
        break;
      case 'last-3-months':
        setDataInizio(startOfMonth(subMonths(now, 2)));
        setDataFine(endOfMonth(now));
        break;
      default:
        break;
    }
  };

  const clearFilters = () => {
    setActiveFilter('this-month');
    setDataInizio(startOfMonth(new Date()));
    setDataFine(endOfMonth(new Date()));
  };

  useEffect(() => {
    loadUscite();
  }, [dataInizio, dataFine]);

  const loadUscite = async () => {
    setLoading(true);
    try {
      const data = await usciteApi.getAll(
        0, 1000,
        format(dataInizio, 'yyyy-MM-dd'),
        format(dataFine, 'yyyy-MM-dd')
      );
      setUscite(data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Non hai i permessi per visualizzare le uscite');
      } else {
        toast.error('Errore nel caricamento');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.importo || parseFloat(formData.importo) <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        data_spesa: format(formData.data_spesa, 'yyyy-MM-dd'),
        importo: parseFloat(formData.importo),
        metodo_pagamento: formData.metodo_pagamento,
        categoria: formData.categoria,
        descrizione: formData.descrizione,
        collegata_a_turno: formData.collegata_a_turno
      };

      if (editingId) {
        await usciteApi.update(editingId, payload);
        toast.success('Uscita aggiornata');
      } else {
        await usciteApi.create(payload);
        toast.success('Uscita registrata');
      }

      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadUscite();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (uscita) => {
    setFormData({
      data_spesa: new Date(uscita.data_spesa),
      importo: uscita.importo.toString(),
      metodo_pagamento: uscita.metodo_pagamento,
      categoria: uscita.categoria,
      descrizione: uscita.descrizione || '',
      collegata_a_turno: uscita.collegata_a_turno
    });
    setEditingId(uscita.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa uscita?')) return;
    try {
      await usciteApi.delete(id);
      toast.success('Uscita eliminata');
      loadUscite();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({
      data_spesa: new Date(),
      importo: '',
      metodo_pagamento: 'contanti',
      categoria: 'Altro',
      descrizione: '',
      collegata_a_turno: 'non specificato'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const totaleUscite = uscite.reduce((sum, u) => sum + u.importo, 0);

  // Group by date
  const groupedUscite = uscite.reduce((acc, uscita) => {
    const date = uscita.data_spesa;
    if (!acc[date]) acc[date] = [];
    acc[date].push(uscita);
    return acc;
  }, {});

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/')}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold font-heading tracking-tight">Uscite di Cassa</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showFilters ? "default" : "outline"}
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="filter-toggle-btn"
              >
                <Filter className="w-4 h-4" />
              </Button>
              {canModify && (
                <Button 
                  size="sm" 
                  onClick={() => { resetForm(); setEditingId(null); setShowForm(true); }}
                  data-testid="add-uscita-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nuova
                </Button>
              )}
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mb-4 p-4 bg-secondary/50 rounded-xl space-y-4 animate-slide-up">
              {/* Quick Filters */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant={activeFilter === 'this-month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setQuickFilter('this-month')}
                  data-testid="filter-this-month"
                >
                  Questo mese
                </Button>
                <Button 
                  variant={activeFilter === 'last-month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setQuickFilter('last-month')}
                  data-testid="filter-last-month"
                >
                  Mese scorso
                </Button>
                <Button 
                  variant={activeFilter === 'last-3-months' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setQuickFilter('last-3-months')}
                  data-testid="filter-last-3-months"
                >
                  Ultimi 3 mesi
                </Button>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Da</p>
                  <Popover open={calendarOpenInizio} onOpenChange={setCalendarOpenInizio}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal h-10"
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        {format(dataInizio, 'd MMM', { locale: it })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dataInizio}
                        onSelect={(date) => {
                          if (date) {
                            setDataInizio(date);
                            setActiveFilter('custom');
                          }
                          setCalendarOpenInizio(false);
                        }}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">A</p>
                  <Popover open={calendarOpenFine} onOpenChange={setCalendarOpenFine}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal h-10"
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        {format(dataFine, 'd MMM', { locale: it })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dataFine}
                        onSelect={(date) => {
                          if (date) {
                            setDataFine(date);
                            setActiveFilter('custom');
                          }
                          setCalendarOpenFine(false);
                        }}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          )}

          {/* Period Summary */}
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600">
                    Totale uscite {activeFilter === 'this-month' ? 'questo mese' : 
                                  activeFilter === 'last-month' ? 'mese scorso' :
                                  activeFilter === 'last-3-months' ? 'ultimi 3 mesi' : 'periodo'}
                  </p>
                  <p className="text-xl font-bold font-mono-numbers text-red-700" data-testid="totale-uscite">
                    {formatCurrency(totaleUscite)}
                  </p>
                </div>
                <Wallet className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-lg mx-auto space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : uscite.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Wallet className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Nessuna uscita registrata</h3>
              <p className="text-muted-foreground mb-4">Registra la prima uscita di cassa</p>
              {canModify && (
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova uscita
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedUscite).map(([date, items]) => (
            <div key={date}>
              <p className="text-sm font-semibold text-muted-foreground mb-2">
                {format(new Date(date), 'd MMMM yyyy', { locale: it })}
              </p>
              <div className="space-y-2">
                {items.map((uscita) => {
                  const categoria = CATEGORIE.find(c => c.value === uscita.categoria);
                  const metodo = METODI_PAGAMENTO.find(m => m.value === uscita.metodo_pagamento);
                  const MetodoIcon = metodo?.icon || HelpCircle;
                  
                  return (
                    <Card 
                      key={uscita.id}
                      data-testid={`uscita-item-${uscita.id}`}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => canModify && handleEdit(uscita)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{categoria?.icon || '📋'}</span>
                            <div>
                              <p className="font-semibold">{uscita.categoria}</p>
                              <p className="text-sm text-muted-foreground truncate max-w-[180px]">
                                {uscita.descrizione || 'Nessuna descrizione'}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                <MetodoIcon className="w-3 h-3" />
                                <span>{metodo?.label}</span>
                                {uscita.collegata_a_turno !== 'non specificato' && (
                                  <>
                                    <span>•</span>
                                    <span>{uscita.collegata_a_turno}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold font-mono-numbers text-red-600">
                              -{formatCurrency(uscita.importo)}
                            </p>
                            {canModify && isAdmin() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive mt-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(uscita.id);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Uscita' : 'Nuova Uscita'}</DialogTitle>
            <DialogDescription>
              Registra una spesa o uscita di cassa
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Data */}
            <div className="space-y-2">
              <Label>Data spesa</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" data-testid="uscita-date-btn">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {format(formData.data_spesa, 'd MMMM yyyy', { locale: it })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.data_spesa}
                    onSelect={(date) => date && setFormData({ ...formData, data_spesa: date })}
                    locale={it}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Importo */}
            <div className="space-y-2">
              <Label>Importo (€)</Label>
              <Input
                data-testid="uscita-importo"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                value={formData.importo}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty, numbers, and decimal with comma or point
                  if (value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                    setFormData({ ...formData, importo: value.replace(',', '.') });
                  }
                }}
                className="text-xl font-mono-numbers"
              />
            </div>

            {/* Categoria */}
            <div className="space-y-2">
              <Label>Categoria</Label>
              <div className="grid grid-cols-4 gap-2">
                {CATEGORIE.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, categoria: cat.value })}
                    className={cn(
                      "p-2 rounded-lg border-2 text-center transition-all",
                      formData.categoria === cat.value
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <span className="text-xl">{cat.icon}</span>
                    <p className="text-[10px] mt-1 truncate">{cat.value.split('/')[0]}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Metodo pagamento */}
            <div className="space-y-2">
              <Label>Metodo pagamento</Label>
              <Select
                value={formData.metodo_pagamento}
                onValueChange={(value) => setFormData({ ...formData, metodo_pagamento: value })}
              >
                <SelectTrigger data-testid="uscita-metodo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METODI_PAGAMENTO.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      <div className="flex items-center gap-2">
                        <m.icon className="w-4 h-4" />
                        {m.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Turno */}
            <div className="space-y-2">
              <Label>Collegata a turno</Label>
              <Select
                value={formData.collegata_a_turno}
                onValueChange={(value) => setFormData({ ...formData, collegata_a_turno: value })}
              >
                <SelectTrigger data-testid="uscita-turno">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TURNI.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Descrizione */}
            <div className="space-y-2">
              <Label>Descrizione (opzionale)</Label>
              <Textarea
                data-testid="uscita-descrizione"
                placeholder="Dettagli sulla spesa..."
                value={formData.descrizione}
                onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-uscita-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? 'Aggiorna' : 'Salva')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsciteCassaPage;
